import React, { useEffect, useState } from 'react';
import { type VariantProps } from 'class-variance-authority';
import { cn } from '../../../utils/cn';

import { ReactComponent as APLogoWhite } from '../../../icons/svg/logo-new-white.svg';
import { ReactComponent as APLogoBlack } from '../../../icons/svg/logo-new-black.svg';
import { ReactComponent as APLogoText } from '../../../icons/svg/logo-text-navbar.svg';
import { Button } from '../../../components/button';
import { CategoriesMenu } from './CategoriesMenu';
import { UserCart } from './UserCart';
import {
    _headerVariants,
    HeaderCartInfo,
    type HeaderCountryElement,
    type HeaderNavLinks,
    type HeaderUserData,
} from './Header';
import { WarningBanner } from '../../../ui/warning-banner/components/WarningBanner';
import { DesktopUserMenu } from './user-menu/DesktopUserMenu';
import { RoccoLink } from '../../../components/link/RoccoLink';
import { HeaderSearch, type HeaderSearchTrackingConfig } from './HeaderSearch';
import type { SearchSuggestionResultGroup } from '../../search/view-models/search-suggestion';
import { CountrySelectMenu } from './CountrySelectMenu';

interface DesktopHeaderProps extends VariantProps<typeof _headerVariants> {
    userData: HeaderUserData;
    primaryLinks: HeaderNavLinks;
    currentCountryCode: string;
    countries: HeaderCountryElement[];
    currentUrl: string;
    onLogout: () => void;
    onSearchClick?: () => void;
    onNavigate?: (url: string) => void;
    onUserCartClick?: () => void;
    onLoginClick: () => void;
    additionalUserMenuItems?: HeaderNavLinks;
    warningMessages?: string[];
    cartInfo: HeaderCartInfo;
    headerSearchTrackingConfig?: HeaderSearchTrackingConfig;
    fetchSuggestions?: (query: string) => void;
    suggestionData?: SearchSuggestionResultGroup | null;
    uiUpdatesJune2025Enabled?: boolean;
}

export const DesktopHeader = (props: DesktopHeaderProps) => {
    const {
        color,
        userData,
        primaryLinks,
        currentCountryCode,
        countries,
        currentUrl,
        onLogout,
        onNavigate,
        onUserCartClick,
        onLoginClick,
        additionalUserMenuItems,
        warningMessages,
        cartInfo,
        uiUpdatesJune2025Enabled,
        headerSearchTrackingConfig,
        fetchSuggestions,
        suggestionData,
    } = props;

    const [isScrolled, setIsScrolled] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);
    const [showTextLogo, setShowTextLogo] = useState(false);
    const [isFadingOut, setIsFadingOut] = useState(false);
    const [shouldRenderMenu, setShouldRenderMenu] = useState(true);

    useEffect(() => {
        const handleScroll = () => {
            const scrollPosition = window.scrollY;
            // Handle logo animation on scroll so next logo doesn't appear
            // until first one finishes animating
            if (scrollPosition > 0 && !isScrolled) {
                setIsAnimating(true);
                setTimeout(() => {
                    setShowTextLogo(true);
                    setIsAnimating(false);
                }, 200);
            } else if (scrollPosition <= 0 && isScrolled) {
                // using <= 0 because on tablets/phones scrolling can go lower 0 and animation wont play

                // First trigger the fade out animation
                setIsFadingOut(true);
                // After fade out completes, hide the text logo and start expand animation
                setTimeout(() => {
                    setShowTextLogo(false);
                    setIsFadingOut(false);
                    setIsAnimating(true);
                    setTimeout(() => {
                        setIsAnimating(false);
                    }, 200);
                }, 200);
            }
            setIsScrolled(scrollPosition > 0);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [isScrolled]);

    // Handle menu visibility with opacity transition
    useEffect(() => {
        if (isScrolled && shouldRenderMenu) {
            const timer = setTimeout(() => {
                setShouldRenderMenu(false);
            }, 300); // Match the fade-out animation duration
            return () => clearTimeout(timer);
        } else if (!isScrolled && !shouldRenderMenu) {
            setShouldRenderMenu(true);
        }
    }, [isScrolled, shouldRenderMenu]);

    const mode =
        color === 'transparent' || color === 'none'
            ? uiUpdatesJune2025Enabled &&
              (currentUrl.includes('/app') || currentUrl.includes('/member'))
                ? 'default'
                : 'float'
            : 'default';

    const menuColor =
        color === 'transparent'
            ? uiUpdatesJune2025Enabled &&
              (currentUrl.includes('/app') || currentUrl.includes('/member'))
                ? 'dark'
                : 'light'
            : 'dark';

    // TODO: Remove this when full screen banner is implemented because then it will use light theme
    // and no longer need the padding block to be there
    const isDetailPageUrl =
        currentUrl.includes('/project/') ||
        currentUrl.includes('/product/') ||
        currentUrl.includes('/professional/');

    const { cartCount } = cartInfo;

    return (
        <>
            {/* Placeholder div with same height as navbar so that
            page elements don't jump up when navbar becomes fixed on scroll */}
            {uiUpdatesJune2025Enabled && isScrolled && !isDetailPageUrl && (
                <div
                    className={cn(
                        'h-48 w-screen',
                        menuColor === 'light' && 'h-0', //Light menu sits on a full banner so it doesn't need this block
                        (currentUrl.includes('/app') ||
                            currentUrl.includes('/member')) &&
                            'bg-[#f6f6f6]' // Member/App pages need the gray background
                    )}
                />
            )}
            <div
                className={cn(
                    _headerVariants({ color, mode }),
                    'md:rocco-header z-50 h-48 w-full',
                    menuColor === 'light' &&
                        'qhd:left-1/2 qhd:-translate-x-1/2',
                    uiUpdatesJune2025Enabled &&
                        (currentUrl.includes('/app') ||
                            currentUrl.includes('/member')) && // On member/app pages, give navbar the same bg-color as the page
                        'relative bg-[#f6f6f6]', // as new navbar wouldn't have a background on these pages and will appear white.
                    isScrolled && [
                        'before:backdrop-hack fixed left-0 right-0 top-0 h-28 transition-all duration-200 before:backdrop-blur-xl',
                        menuColor === 'dark'
                            ? '!before:bg-gray-100/20 !bg-gray-100/20'
                            : '!before:bg-black/30 !bg-black/30',
                        'qhd:left-0 qhd:translate-x-0',
                    ],
                    'grid grid-cols-[auto_1fr_auto] items-center gap-x-20 px-12 transition-[height] duration-200 ease-in-out lg:gap-x-16 qhd:mx-auto qhd:max-w-[1920px] qhd:px-12'
                )}
            >
                {/* Full-width background for app/member pages */}
                {uiUpdatesJune2025Enabled &&
                    !isScrolled &&
                    (currentUrl.includes('/app') ||
                        currentUrl.includes('/member')) && (
                        <div className="fixed left-0 top-0 -z-10 h-48 w-screen bg-[#f6f6f6]" />
                    )}

                {/* Left Column - Logo */}
                <div
                    className={cn(
                        'flex items-center transition-[height] duration-200 ease-in-out',
                        isScrolled ? 'h-28' : 'h-48'
                    )}
                >
                    <RoccoLink
                        to="/"
                        prefetch="intent"
                        aria-label="Home"
                        className={cn(
                            color == 'transparent'
                                ? 'text-gray-50'
                                : 'text-gray-900',
                            'block w-[40px] sm:w-[50px] md:w-[60px] lg:w-[80px] xl:w-[110px] 2xl:w-[140px]'
                        )}
                    >
                        {showTextLogo ? (
                            <APLogoText
                                className={cn(
                                    'w-[85%] sm:w-auto',
                                    isFadingOut
                                        ? 'h-6 w-auto animate-right-to-left-fade-out'
                                        : 'h-6 w-auto animate-left-to-right-fade-in',
                                    menuColor === 'dark'
                                        ? 'brightness-0'
                                        : 'brightness-0 invert'
                                )}
                                style={{
                                    WebkitMaskRepeat: 'no-repeat',
                                    WebkitMaskImage:
                                        'linear-gradient(to right, rgba(0, 0, 0, 1) 85%, rgba(0, 0, 0, 1))',
                                }}
                            />
                        ) : (
                            <div
                                style={{
                                    WebkitMaskRepeat: 'no-repeat',
                                    WebkitMaskSize: isScrolled ? '100%' : '0%',
                                    animation: isAnimating
                                        ? isScrolled
                                            ? 'shrink-logo 0.3s ease-in forwards'
                                            : 'expand-logo 0.1s ease-in forwards'
                                        : 'none',
                                    transform: isAnimating
                                        ? 'scale(0)'
                                        : 'scale(1)',
                                    transition: isScrolled
                                        ? 'transform 0.3s ease-in'
                                        : 'transform 0.1s ease-in, opacity 0.1s ease-in',
                                    opacity: isAnimating && !isScrolled ? 0 : 1,
                                }}
                            >
                                {menuColor === 'dark' ? (
                                    <APLogoBlack className="h-full w-full" />
                                ) : (
                                    <APLogoWhite className="h-full w-full" />
                                )}
                            </div>
                        )}
                    </RoccoLink>
                </div>

                {/* Second Column - Search and Categories */}
                <div
                    className={cn(
                        'relative grid h-[65%] w-full items-center transition-all duration-300',
                        isScrolled ? 'grid-rows-1' : 'grid-rows-[1fr_1fr]'
                    )}
                >
                    {/* Top Row - Search Bar */}
                    <div
                        className={cn(
                            'relative z-30 flex w-full items-center',
                            isScrolled ? 'absolute' : ''
                        )}
                    >
                        <HeaderSearch
                            onNavigate={(url) => {
                                if (onNavigate) {
                                    onNavigate(url);
                                } else {
                                    window.location.href = url;
                                }
                            }}
                            displayMode="desktop"
                            placeholderTexts={['Search ArchiPro']}
                            menuColor={menuColor}
                            trackingConfig={headerSearchTrackingConfig}
                            fetchSuggestions={fetchSuggestions}
                            suggestionData={suggestionData}
                        />
                    </div>

                    {/* Bottom Row - Categories Menu */}
                    {shouldRenderMenu && (
                        <div
                            className={cn(
                                'relative z-0 -mt-5 flex w-full justify-start pl-6 transition-all duration-300 ease-in-out',
                                isScrolled
                                    ? 'animate-fade-out-bottom'
                                    : 'animate-fade-in-bottom'
                            )}
                        >
                            <CategoriesMenu
                                color={menuColor}
                                links={primaryLinks}
                                currentUrl={currentUrl}
                                uiUpdatesJune2025Enabled={
                                    uiUpdatesJune2025Enabled
                                }
                            />
                        </div>
                    )}
                </div>

                {/* Third Column - Buttons and Empty Row */}
                <div
                    className={cn(
                        'relative grid h-[65%] w-full items-center transition-all duration-300 ease-in-out',
                        isScrolled ? 'grid-rows-1' : 'grid-rows-[1fr_1fr]'
                    )}
                >
                    {/* Buttons and Country Select Menu */}
                    <div
                        className={cn(
                            'flex items-center justify-end gap-x-10 transition-all duration-300 ease-in-out'
                        )}
                    >
                        <CountrySelectMenu
                            color={menuColor}
                            currentCountryCode={currentCountryCode}
                            countries={countries}
                        />

                        {userData.isAuthorized ? (
                            <>
                                {cartCount > 0 ? (
                                    <UserCart
                                        fill={
                                            menuColor === 'dark'
                                                ? 'dark'
                                                : 'light'
                                        }
                                        onUserCartClick={() => {
                                            onUserCartClick?.();
                                        }}
                                        cartInfo={cartInfo}
                                    />
                                ) : null}

                                <DesktopUserMenu
                                    userData={userData}
                                    color={menuColor}
                                    onLogout={onLogout}
                                    additionalUserMenuItems={
                                        additionalUserMenuItems
                                    }
                                />
                            </>
                        ) : (
                            <>
                                {cartCount > 0 ? (
                                    <UserCart
                                        fill={
                                            menuColor === 'dark'
                                                ? 'dark'
                                                : 'light'
                                        }
                                        onUserCartClick={() => {
                                            onUserCartClick?.();
                                        }}
                                        cartInfo={cartInfo}
                                    />
                                ) : null}
                                <RoccoLink
                                    to={'#'}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        onLoginClick();
                                    }}
                                    aria-label="Log In"
                                    className={cn(
                                        'flex min-w-18 items-center text-base font-medium decoration-2 underline-offset-8 hover:underline md:min-w-14',
                                        color === 'transparent'
                                            ? 'text-gray-50 hover:text-gray-50'
                                            : 'text-gray-900 hover:text-gray-900'
                                    )}
                                >
                                    Log In
                                </RoccoLink>
                                <div>
                                    <Button
                                        color="dark"
                                        weight="default"
                                        size={'default'}
                                        onClick={onLoginClick}
                                        className="min-w-32"
                                    >
                                        Sign Up
                                    </Button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {!!warningMessages?.length && (
                <div
                    className={cn(
                        'w-screen qhd:left-1/2 qhd:max-w-[1920px] qhd:-translate-x-1/2',
                        isScrolled
                            ? 'fixed top-28 z-40'
                            : menuColor === 'light' ||
                                currentUrl.includes('/project/') //TODO: Remove this when new project detail page is up
                              ? 'absolute left-0 top-48 z-40'
                              : 'relative mt-2'
                    )}
                >
                    <WarningBanner alerts={warningMessages} />
                </div>
            )}
        </>
    );
};
